import React from 'react';
import { IStyle, mergeStyleSets } from '@fluentui/react';
import { TextureSwatch } from '../texture-swatch';
import { remove } from '../../../framework/utils/collection';

export interface ITextureSelectorProps<TOption = any, TValue = any> {
  swatchSize: number,
  options: TOption[];
  selected: TValue[];
  allowMultipleSelection: boolean;
  allowNoSelection: boolean;
  showLabels: boolean;
  getSwatchKey: (option: TOption) => string; /**Unique identifier for swatch */
  getColorOrImage: (option: TOption) => string;
  getValue: (option: TOption) => TValue;
  getLabel: (option: TOption) => string;
  maxOptionDisplay?: number | undefined;
  selectAllOptionsValue?: TValue | undefined; /** i.e. a value in the enum meaning 'I choose all texture options', if such a value exists. This value has no effect when allowMultipleSelection is false */
  onSelectionChanged?: (selected: TValue[]) => void | undefined;
  rootStyle?: React.CSSProperties | undefined;
}

const getStyleClasses = () => {
  const root: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '0.3em',
  };

  return mergeStyleSets({
    root,
  });
}

export const UiTextureSelector: React.FunctionComponent<ITextureSelectorProps> = (props) => {
  const { options, selected, onSelectionChanged } = props;
  const displayOptions = !!props.maxOptionDisplay && options.length > props.maxOptionDisplay
    ? options.slice(0, props.maxOptionDisplay) : options;
  const unseenCount = options.length - displayOptions.length;
  const cs = getStyleClasses();
  const fontSize = `${props.swatchSize}rem`;
  const rootStyle = !!props.rootStyle ? {...props.rootStyle, fontSize} : { fontSize };

  const handleSingleSelection = (texture: any) => {
    if(!props.onSelectionChanged) { return; } // will never happen

    const prevSelection = selected.length === 1 ? selected[0] : undefined;
    const currentSelection = props.getValue(texture);
    if(currentSelection !== prevSelection) {
      props.onSelectionChanged([currentSelection]);
    } else if (props.allowNoSelection) {
      props.onSelectionChanged([]);
    }
  }

  const handleMultipleSelection = (texture: any) => {
    if(!props.onSelectionChanged) { return; } // will never happen

    const caValue = props.selectAllOptionsValue;
    const noCaValue = caValue === undefined;
    const selection = props.getValue(texture);
    if(noCaValue || selection !== caValue) { // there is no 'choose all' (CA) value, or the CA value wasn't chosen
      if(selected.indexOf(selection) === -1) { // adding specific texture to the selection set
        if(noCaValue || selected.indexOf(caValue) !== 0) { // CA value not in existing selection
          const selections = [...selected, selection];
          props.onSelectionChanged(selections);
        } else { // selecting a specific texture deselects the existing CA value
          props.onSelectionChanged([selection])
        }
      } else { // deselecting specific texture
        const selections = remove(selected, s => s === selection);
        
        if(props.allowNoSelection || selections.length > 0) { // if empty selection allowed, or there are selections, value is whatever we have here
          props.onSelectionChanged(selections);
        } else if (noCaValue === false) { // if we must have a selection and there is a CA value, we select it
          props.onSelectionChanged([caValue]);
        } else { 
          // if we must have a selection and there is no CA value, we can't change anything
          return; // for clarity
        }
      }
    } else { // CA value was selected
      if(selected[0] === caValue && props.allowNoSelection) { // existing selection was already CA value. If no selection allowed, clear the selection
        props.onSelectionChanged([]);
      } else { // CA selection is new to the list
        props.onSelectionChanged([caValue]);
      }
    }
  }

  const onTextureClicked = (texture: any) => {
    if(!props.onSelectionChanged) { return; } // will never happen

    if(props.allowMultipleSelection) {
      handleMultipleSelection(texture);
    } else {
      handleSingleSelection(texture);
    }
  }

  return (
    <div className={cs.root} style={rootStyle}>
      {displayOptions.map((swatch, index) =>
      <TextureSwatch
        key={props.getSwatchKey(swatch)}
        id={props.getSwatchKey(swatch)}
        tabIndex={!!onSelectionChanged ? index : undefined}
        texture={swatch}
        selected={selected.indexOf(props.getValue(swatch)) > -1}
        showLabel={props.showLabels}
        getColorOrImage={props.getColorOrImage}
        getLabel={props.getLabel}
        onSelected={!!onSelectionChanged ? onTextureClicked : undefined}
      />)}
      {unseenCount > 0 &&
      <span aria-label={`Plus ${unseenCount} additional options`}>{`+${unseenCount}`}</span>}
    </div>
  );
};
